import React, { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import { eventBus } from '@atc/cai-event-bus';
import { formatCurrency } from '@atc/string-fns';

import { Box, Button, InfoIcon, Link, LoadingSpinner, Text } from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { myWalletViewDealStatusCTAClick } from 'reaxl-analytics-handlers';
import { EmailCTA } from 'reaxl-email';
import { EsntialBuyNowCTA, PaymentError, PrequalCTA } from 'reaxl-wallet';

import paymentsDuck from '@/ducks/paymentsDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import usePaymentData from '@/hooks/usePaymentData';

function renderFinancingDetails({ interestRate, lender, loanTerm }) {
    return (
        <div className="display-flex flex-wrap">
            <Text
                weight="bold"
                className="margin-right-2"
            >
                {lender}
            </Text>
            {`${interestRate}% APR for ${loanTerm} mo.`}
        </div>
    );
}

function renderLeasingDetails({ downPayment, loanTerm }) {
    return `for ${loanTerm} months, ${formatCurrency(downPayment)} due at signing`;
}

function renderOffer({ offer, isEV, offersModalCTA }) {
    return (
        <div className="display-flex flex-wrap gap-2">
            <Text
                color="success"
                weight="bold"
            >
                {`${formatCurrency(offer)} Applied`}
            </Text>

            {isEV && typeof offer === 'number' && offer === 7500 && (
                <span>
                    <Text> Includes EV Tax Credit </Text>
                    <InfoIcon
                        popoverId="evOffersInfo"
                        trigger={['click']}
                        infoText={(
                            <Text>
                                Talk with the dealer about how to use this credit towards your down payment.
                                {offersModalCTA}
                            </Text>
                        )}
                        placement="top"
                    />
                </span>
            )}
        </div>
    );
}

function renderOffersAndIncentives({ showSpecialAPR, offer, interestRate, lender, loanTerm, isEV, offersModalCTA }) {
    return (
        <>
            <Text
                weight="bold"
                className="display-block"
            >
                Special Offers and Incentives
            </Text>
            {offer && renderOffer({ offer, isEV, offersModalCTA })}
            {showSpecialAPR && (
                <div className="display-flex flex-wrap">
                    <Text
                        color="success"
                        weight="bold"
                        className="margin-right-2"
                    >
                        {`${interestRate}% APR for ${loanTerm} mo.`}
                    </Text>
                    {lender}
                </div>
            )}
        </>
    );
}

function renderPaymentDetails({ currentTab, isOfferOrIncentive, showSpecialAPR, offer, interestRate, lender, loanTerm, downPayment, isLoading, isEV, offersModalCTA }) {
    let isPaymentLoading = isLoading;

    if (!isOfferOrIncentive) {
        switch (currentTab) {
            case 'finance':
                isPaymentLoading = isLoading || !interestRate || !loanTerm;
                break;
            case 'lease':
                isPaymentLoading = isLoading || !loanTerm && (!downPayment && downPayment !== 0);
                break;
            default:
                break;
        }
    }

    return (
        <>
            {isPaymentLoading && (
                <LoadingSpinner
                    className="margin-top-0"
                    height={24}
                />
            )}
            {!isPaymentLoading && !isOfferOrIncentive && currentTab === 'finance' && renderFinancingDetails({ interestRate, lender, loanTerm })}
            {!isPaymentLoading && !isOfferOrIncentive && currentTab === 'lease' && renderLeasingDetails({ downPayment, loanTerm })}
            {!isPaymentLoading && isOfferOrIncentive && renderOffersAndIncentives({ showSpecialAPR, offer, interestRate, lender, loanTerm, isEV, offersModalCTA })}
        </>
    );
}

function renderPSXCreditAppCTA({ onCTAClick, url, ctaLabel, partner }) {
    return (
        <Link
            href={url}
            onClick={onCTAClick}
            data-cmp={`credit-app-${partner}`}
            undecorated
        >
            {ctaLabel}
        </Link>
    );
}

/**
 *  The DealBox component is used to display information about deals and financing of a listing.
 *  If the data is present, it will also display incentives and special offers.
 */
function DealBox({
    displayPaymentError = true,
    onCreditAppCTAClick,
    paymentCardCTAProps,
    suppressPriceCardCTA,
    initialDealType = 'finance',
    emailCTAProps,
    supportedDealTypes = ['finance', 'cash'],
}) {
    const dispatch = useDispatch();

    const [isLoading, hasError, paymentData] = usePaymentData(supportedDealTypes);
    const [currentTab, setCurrentTab] = useState(initialDealType);
    const [currentTabOffersObj, setCurrentTabOffersObj] = useState({});
    const [offerObjError, setOfferObjError] = useState(false);

    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory) || {};
    const {
        creditApp,
        ecommWebsite = {},
        engine,
        fuelType,
        id: listingId,
        owner = {},
        zip,
        marketExtension: { parentOwnerId: parentId } = {},
        esntial,
        vin,
    } = listing;

    const isEV = fuelType?.name === 'Electric' || engine?.name === 'Electric';

    const {
        dealsDuck,
        enableSDPCreditApp,
        enableSACAPriceCard,
        handleSACACreditAppClick = () => { },
        ...digitalRetailProps
    } = paymentCardCTAProps;

    const existingDeal = useSelector((state) => dealsDuck.selectors.getDealByListingVin(state, vin)) || {};

    const listingType = (listing.type || listing.listingType)?.toLowerCase() === 'new' ? 'new' : 'used';

    const paymentsInfo = useSelector(paymentsDuck.selectors.getPaymentsInfo);
    digitalRetailProps.paymentsInfo = paymentsInfo;

    const {
        loanTerm,
        offeredBy: lender,
        downPayment,
        showSpecialAPR = false,
        pricingDetails = {},
    } = currentTabOffersObj;
    const interestRate = currentTabOffersObj[listingType] || currentTabOffersObj?.interestRate;
    const offer = pricingDetails?.rebate ?? null;
    const isOfferOrIncentive = showSpecialAPR || !!offer;
    const isEmailCTA = !!emailCTAProps && !!Object.keys(emailCTAProps).length;

    const { id: existingDealId } = existingDeal;
    const isExistingDeal = !!existingDealId;

    const showPsxCreditAppCTA = creditApp?.partner === 'psx';
    const isSDPOrSACA = enableSDPCreditApp || enableSACAPriceCard;

    const enableEsntialCTA = esntial
        && !esntial?.deemphasized
        && Object.keys(ecommWebsite).length > 0;

    const ownerTrackingId = parentId ?? (typeof owner === 'number' ? owner : owner.id);

    const isCTAEnabled = enableEsntialCTA || isSDPOrSACA || isEmailCTA;
    const hideDealBox = currentTab === 'cash'
        && !showPsxCreditAppCTA
        && !isCTAEnabled;

    useEffect(() => {
        if (paymentData && currentTab) {
            setOfferObjError(false);
            const offersObj = paymentData.find((item) => item?.paymentLabel?.toLowerCase() === currentTab);
            if (offersObj?.isPaymentError) {
                setOfferObjError(true);
            }
            setCurrentTabOffersObj(offersObj);
        }
    }, [paymentData, currentTab]);

    useEffect(() => {
        const handleCalcPencilingDataUpdateRequest = (data) => {
            setCurrentTab(data.dealType);
        };

        eventBus.on(
            'uds:calcPencilingDataUpdate:updated',
            handleCalcPencilingDataUpdateRequest
        );

        return () => {
            eventBus.off(
                'uds:calcPencilingDataUpdate:updated',
                handleCalcPencilingDataUpdateRequest
            );
        };
    }, [paymentData, setCurrentTab]);

    const updateMyWallet = useCallback((data) => {
        dispatch(paymentsDuck.creators.updateMyWallet(data));
    }, [dispatch]);

    const viewDealClickHandler = (event) => {
        sendClick(myWalletViewDealStatusCTAClick, event, { placement: 'price card' });
        updateMyWallet({
            displayModal: true,
            selectedPanelName: 'activeDeals',
        });
    };

    const offersModalCTA = (
        <EmailCTA
            className="display-inline-block margin-left-1"
            listingId={listingId}
            type="link"
            context="incentives"
            labelKey="contactDealer"
            onClick={!!emailCTAProps && emailCTAProps.onClick}
        />
    );

    return (
        <>
            {(displayPaymentError && (hasError || offerObjError)) && (
                <PaymentError className="margin-bottom-4" />
            )}
            {!hasError && !offerObjError && !hideDealBox && (
                <Box
                    className="text-subdued margin-bottom-4 display-flex flex-column gap-2"
                    data-cmp="deal-box"
                    padding="compact"
                    backgroundColor="neutral-secondary"
                >
                    {!isExistingDeal && (
                        <>
                            {renderPaymentDetails({ currentTab,
                                isOfferOrIncentive,
                                showSpecialAPR,
                                offer,
                                interestRate,
                                lender,
                                loanTerm,
                                downPayment,
                                isLoading,
                                isEV,
                                offersModalCTA })}

                            {showPsxCreditAppCTA && renderPSXCreditAppCTA({ ...creditApp, onCTAClick: onCreditAppCTAClick })}

                            {isCTAEnabled && (
                                <div className="display-flex">
                                    {enableEsntialCTA && (
                                        <EsntialBuyNowCTA
                                            {...ecommWebsite}
                                            dealsDuck={dealsDuck}
                                            analyticsData={{
                                                listingId,
                                                placement: 'dealer-card',
                                            }}
                                            bsStyle="text"
                                            className="text-size-300"
                                            ownerTrackingId={ownerTrackingId}
                                            paymentsDuck={paymentsDuck}
                                            zip={zip || owner?.location?.address?.zip}
                                        />
                                    )}
                                    {!enableEsntialCTA && isEmailCTA && (
                                        <EmailCTA
                                            listingId={listingId}
                                            type="link"
                                            context="incentives"
                                            labelKey="seeAllOffers"
                                            onClick={emailCTAProps.onClick}
                                        />
                                    )}
                                    {!enableEsntialCTA && isSDPOrSACA && (
                                        <PrequalCTA
                                            digitalRetailProps={digitalRetailProps}
                                            handleSACACreditAppClick={handleSACACreditAppClick}
                                            enableSDPCreditApp={enableSDPCreditApp}
                                            enableSACACreditApp={enableSACAPriceCard}
                                            existingDeal={existingDeal}
                                            dealsDuck={dealsDuck}
                                            label="Apply for Financing"
                                            placement="payment-card"
                                            suppressPriceCardCTA={suppressPriceCardCTA}
                                            hideSeparator={!isEmailCTA}
                                        />
                                    )}
                                </div>
                            )}
                        </>
                    )}
                    {isExistingDeal && (
                        <>
                            <Text
                                weight="bold"
                                className="display-block"
                            >
                                Your offer has been sent!
                            </Text>
                            <Button
                                bsStyle="text"
                                onClick={viewDealClickHandler}
                            >
                                View Your Offer
                            </Button>
                        </>
                    )}
                </Box>
            )}
        </>
    );
}

DealBox.propTypes = {
    displayPaymentError: PropTypes.bool,
    /**
     * Callback function when any credit app CTA is clicked
     */
    onCreditAppCTAClick: PropTypes.func,
    /**
     * Object containing properties for the payment card CTA
     */
    paymentCardCTAProps: PropTypes.shape({
        /**
         * Existing deal details
         */
        existingDeal: PropTypes.object,
        /**
         * Listing details
         */
        listing: PropTypes.object,
        /**
         * Deals duck object
         */
        dealsDuck: PropTypes.object,
        /**
         * Payments duck object
         */
        paymentsDuck: PropTypes.object,
        /**
         * Flag to enable SDP Credit App
         */
        enableSDPCreditApp: PropTypes.bool,
        /**
         * Flag to enable SACA Price Card
         */
        enableSACAPriceCard: PropTypes.bool,
        /**
         * Function to handle SACA Credit App click
         */
        handleSACACreditAppClick: PropTypes.func,
    }),
    /**
     * Flag to suppress the price card CTA
     */
    suppressPriceCardCTA: PropTypes.bool,
    /**
     * Initial deal type, default is 'finance'
     */
    initialDealType: PropTypes.string,
    /**
     * Object containing properties for the email CTA
     */
    emailCTAProps: PropTypes.object,
    /**
     * Array containg strings of all the supported deal types
     */
    supportedDealTypes: PropTypes.array,
};

export default DealBox;
